import { FormEvent, useContext, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import SurveyAlternative from './SurveyAlternative';
import IconButton from '../../../../components/atoms/button/IconButton';
import Input from '../../../../components/atoms/input';
import TextArea from '../../../../components/atoms/text-area/TextArea';
import {
  SelectSurveyContentData,
  SelectSurveyContentSpecificData,
  SelectType,
  SurveyAlternativeData,
} from '../../../../types/contentTypes';
import AlertContext from '../../../../utils/AlertContext';
import { ContentFormProps } from '../ContentEditor';
import inputFields from '../inputFields.json';

const DEFAULT_ANSWER_ALTERNATIVES = [
  { key: uuidv4(), text: '' },
  { key: uuidv4(), text: '' },
];

export default function SelectSurveyContentForm({
  content,
  onNewContentSubmit,
  onExistingContentSubmit,
}: ContentFormProps<SelectSurveyContentData, SelectSurveyContentSpecificData>) {
  const [headline, setHeadline] = useState<string>(content?.headline || '');
  const [description, setDescription] = useState<string>(content?.description || '');
  const [alternatives, setAlternatives] = useState<SurveyAlternativeData[]>(
    content?.alternatives || DEFAULT_ANSWER_ALTERNATIVES
  );
  const [selectType, setSelectType] = useState<SelectType>(content?.selectType || SelectType.Single);
  const showDelete = alternatives.length > 2;

  const showAlert = useContext(AlertContext);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!content) {
      onNewContentSubmit?.({
        headline,
        description,
        alternatives,
        selectType,
      });
    } else {
      onExistingContentSubmit?.({
        ...content,
        headline,
        description,
        alternatives,
      });
    }
    showAlert('Changes saved!');
  }

  function handleAnswerChange(value: string, index: number) {
    const newAlternatives = alternatives.map((alternative, i) => {
      if (i === index) {
        return { ...alternative, text: value };
      } else {
        return alternative;
      }
    });
    setAlternatives(newAlternatives);
  }

  function handleAddAlternative() {
    const newAlternatives = [
      ...alternatives,
      {
        key: uuidv4(),
        text: '',
      },
    ];
    setAlternatives(newAlternatives);
  }

  function handleDelete(index: number) {
    const newAnswerAlternatives = alternatives.filter((_, i) => i !== index);
    setAlternatives(newAnswerAlternatives);
  }

  const AnswerAlternatives = alternatives.map((alternative, index) => (
    <li key={alternative.key} className="mb-6">
      <SurveyAlternative
        alternative={alternative}
        index={index}
        onTextChange={value => handleAnswerChange(value, index)}
        onDelete={showDelete ? () => handleDelete(index) : undefined}
      />
    </li>
  ));

  return (
    <form onSubmit={handleSubmit}>
      <div className="self-start">
        <label className="block mb-2">Enter headline</label>
        <Input className="mb-8 w-full" {...inputFields.headline} state={[headline, setHeadline]} />
      </div>
      <label className="block mb-4 font-medium">Survey description</label>
      <TextArea
        className={`w-full mb-8`}
        state={[description, setDescription]}
        placeholder="Enter a description of the video"
        rows={4}
      />
      <label className="block mb-4 font-medium">Allow multiple answers?</label>
      <input
        type="checkbox"
        className="check-box mb-8"
        onChange={() => setSelectType(selectType === SelectType.Single ? SelectType.Multiple : SelectType.Single)}
        checked={selectType === SelectType.Multiple}
      />
      <div className="mb-4 font-medium">Alternatives</div>
      <ol className="mb-8">{AnswerAlternatives}</ol>
      <IconButton className="mb-16" icon="add" label="Add answer alternative" onClick={handleAddAlternative} />
      <IconButton icon="save-outline" label={!content ? 'Save and add to section' : 'Save changes'} type="submit" />
    </form>
  );
}
